import { Box, Chip, Collapse, Grid, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import ShowMoreButton from 'src/App/Dashboards/Market/Components/Buttons/ShowMoreButton';
import { BarDataCard, LoadingCard } from 'src/App/Dashboards/Market/Components';
import NoDataCard from 'src/App/Dashboards/Market/Components/Cards/NoDataCard';
import {
  COLOR_INFO,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_SECONDARY_1_300,
  COLOR_SECONDARY_1_600,
  COLOR_TERTIARY_1,
  COLOR_TERTIARY_1_500,
  COLOR_TERTIARY_1_600,
  COLOR_TERTIARY_1_700,
  COLOR_TERTIARY_1_800,
  GRAPH_BLUE,
} from 'src/utils/colors';
import { amenitiesObject } from 'src/utils/const';
import { useUpgradeMesageProFeature } from 'src/hooks/useUpgradeText';
import { BarGraphData } from 'src/interfaces/idashboard';

const DEMO_DATA: BarGraphData[] = [
  { keyShort: 'Parking', keyLong: 'Parking', value: 3.45 },
  { keyShort: 'Gym', keyLong: 'Gym', value: 27.42 },
  { keyShort: 'Dryer', keyLong: 'Dryer', value: 7.69 },
  { keyShort: 'Washer', keyLong: 'Washer', value: 4.19 },
  { keyShort: 'AC', keyLong: 'Air Conditioning', value: 6.24 },
  { keyShort: 'WiFi', keyLong: 'WiFi', value: 7.92 },
  { keyShort: 'TV', keyLong: 'Television', value: 10 },
  { keyShort: 'BBQ Grill', keyLong: 'BBQ Grill', value: 2.43 },
  { keyShort: 'Fireplace', keyLong: 'Fireplace', value: 1.73 },
  { keyShort: 'Breakfast', keyLong: 'Breakfast', value: 8.91 },
  { keyShort: 'Amazing Views', keyLong: 'Amazing Views', value: 7.74 },
];

const DEMO_LOW_YIELD_AMINITY = ['Hot Tub', 'Pets', 'Pool', 'Kitchen'];

const PremiumOverlay = ({
  isXAxisTickVertical,
  handleClick,
  offsetX,
  offsetY,
  offsetWidth,
  offsetHeight,
}: {
  isXAxisTickVertical: boolean | undefined;
  handleClick: any;
  offsetX?: number;
  offsetY?: number;
  offsetWidth?: number;
  offsetHeight?: number;
}) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: offsetY ? offsetY + 'px' : '50px',
        left: offsetX ? offsetX + 'px' : '50px',
        width: `calc(100% - 30px + ${offsetWidth ? offsetWidth + 'px' : '0px'})`,
        height: `calc(${isXAxisTickVertical ? '150px' : '80%'} + ${
          offsetHeight ? offsetHeight + 'px' : '0px'
        })`,
        background: '#fffa',
        backgroundBlendMode: 'multiply',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Typography
        variant="body1"
        onClick={() => handleClick('expert')}
        sx={{
          fontSize: '20px',
          fontWeight: 500,
          color: COLOR_PRIMARY_2,
          cursor: 'pointer',
          pointerEvents: 'auto',
          textDecoration: 'underline',
        }}
      >
        {useUpgradeMesageProFeature()}
      </Typography>
    </Box>
  );
};

const AmenitiesImpactOnRevenueChart = ({
  revenueImpactOnAmenitiesDistro,
  isDisabled,
  userTailorMarketAccess,
  handleOnSampleClick,
  isFilterActive,
  noDataInMarket,
  cachedMarketData,
  demandByAmenitiesChartRef,
  embedded = false,
}: {
  revenueImpactOnAmenitiesDistro: any;
  isDisabled: boolean;
  userTailorMarketAccess?: any;
  handleOnSampleClick: any;
  isFilterActive?: boolean;
  noDataInMarket?: boolean;
  cachedMarketData: any;
  demandByAmenitiesChartRef: any;
  embedded?: boolean;
}) => {
  const [showAmenitiesWithoutNoticibleRevenue, setShowAmenitiesWithoutNoticibleRevenue] = useState<
    boolean
  >(false);

  const theme = useTheme();

  // const filteredRevenueByAmenitiesGraphData = useSelector(state => (state as any).dashboard.cachedMarketDataComponentLevel?.revenueByAmenitiesGraph);
  // const isFilteredDataLoading = filteredRevenueByAmenitiesGraphData.loading;

  const upgradeMessage = useUpgradeMesageProFeature();

  // const isLoading = cachedMarketData.loading || (isFilteredDataLoading && isFilterActive);
  const isLoading = cachedMarketData.loading;

  if (noDataInMarket) {
    return <NoDataCard height={150} borderRadius={8} />;
  }

  if (isLoading) {
    return <LoadingCard height={150} type="bars" />;
  }

  const processAmenityChartData = () => {
    let amenityChartData: BarGraphData[] = [];
    const amenitiesWithoutNoticibleRevenue: string[] = [];

    // if(isFilterActive && !isFilteredDataLoading) {

    //   if(!filteredRevenueByAmenitiesGraphData.data) {
    //     return {
    //       amenityChartData: [],
    //       amenitiesWithoutNoticibleRevenue: []
    //     }
    //   }

    //   return {
    //     amenityChartData: filteredRevenueByAmenitiesGraphData.data.revenue_by_amenities,
    //     amenitiesWithoutNoticibleRevenue: filteredRevenueByAmenitiesGraphData.data.amenities_without_noticeable_revenue
    //   }
    // }

    for (const amenity in revenueImpactOnAmenitiesDistro) {
      const amenityData: BarGraphData = {
        keyShort: amenitiesObject[amenity],
        keyLong: amenitiesObject[amenity],
        value: revenueImpactOnAmenitiesDistro[amenity],
      };
      amenityChartData.push(amenityData);
    }

    amenityChartData = amenityChartData.filter(amenityGraphObject => {
      if (amenityGraphObject.value < 5) {
        amenitiesWithoutNoticibleRevenue.push(amenityGraphObject.keyLong);
      }
      return amenityGraphObject.value >= 5;
    });

    amenityChartData.sort((a, b) => b.value - a.value);
    return { amenityChartData, amenitiesWithoutNoticibleRevenue };
  };

  const { amenityChartData, amenitiesWithoutNoticibleRevenue } = isDisabled
    ? { amenityChartData: DEMO_DATA, amenitiesWithoutNoticibleRevenue: DEMO_LOW_YIELD_AMINITY }
    : processAmenityChartData();

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      item
      xs={12}
      sx={{
        ...(embedded
          ? { border: '1px solid #E0E0E0', borderRadius: 1 }
          : { boxShadow: '0 4px 10px #0003', borderRadius: 1 }),
        position: 'relative',
        ...theme.utils.cardPadding,
      }}
    >
      <Grid container xs={12} sx={{ padding: '20px' }}>
        <Grid item xs={12}>
          <BarDataCard
            title="Demand by Amenities"
            data={amenityChartData}
            orientation="horizontal"
            unit="%"
            label="%"
            graphHeight={180}
            barColor={GRAPH_BLUE}
            barAccentColor={COLOR_TERTIARY_1_800}
            tooltipTextSuffix={' Amenity Demand Score'}
            parent="revenue_by_amenities"
            tooltipContent="This chart highlights which amenities are most valued by guests in this market, based on their impact on nightly rates and occupancy rates."
            addSignPrefix={true}
            useRefToSaveGrapgCopy={demandByAmenitiesChartRef}
          />
        </Grid>

        <Grid
          container
          xs={12}
          sx={{ marginBottom: showAmenitiesWithoutNoticibleRevenue ? '0' : '-16px' }}
        >
          <ShowMoreButton
            isShowingLess={!showAmenitiesWithoutNoticibleRevenue}
            handleButtonClick={() => setShowAmenitiesWithoutNoticibleRevenue(prev => !prev)}
            additionalStyle={{ marginRight: '-16px' }}
          />
        </Grid>

        <Collapse in={showAmenitiesWithoutNoticibleRevenue}>
          <Grid item xs={12} sx={{ mt: '10px', mb: '10px' }}>
            <Typography variant="body1">Amenities without noticeable demand</Typography>
          </Grid>
          <Grid item xs={12} sx={{ mb: '10px' }}>
            {amenitiesWithoutNoticibleRevenue &&
              amenitiesWithoutNoticibleRevenue.length > 0 &&
              amenitiesWithoutNoticibleRevenue.map(amenitiy => {
                return (
                  <Chip
                    key={`chip-${amenitiy}`}
                    label={amenitiy}
                    variant={'outlined'}
                    sx={{
                      minWidth: 50,
                      marginTop: '10px',
                      marginRight: '5px',
                      borderColor: COLOR_PRIMARY_1,
                      borderWidth: '1px',
                      color: COLOR_PRIMARY_1,
                      fontWeight: 500,
                      '&.MuiChip-filled': {
                        backgroundColor: COLOR_PRIMARY_1,
                        color: '#fff',
                      },
                      // ...styles,
                    }}
                  />
                );
              })}
          </Grid>
        </Collapse>
      </Grid>
      {isDisabled && (
        <PremiumOverlay
          isXAxisTickVertical={true}
          handleClick={() => handleOnSampleClick('expert')}
        />
      )}
    </Grid>
  );
};

export default AmenitiesImpactOnRevenueChart;
